import { Download, FileBarChart, Loader2 } from "lucide-react";
import { useState } from "react";
import {
  reportDownloadUrl,
  useCreateReportExport,
  useReportExports,
  useReportSummary,
} from "@/api/hooks";
import type { ReportExportStatus, ReportFormat, ReportType } from "@/api/types";
import { PageHeader } from "@/components/app-shell";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Field,
  MicroLabel,
  Num,
} from "@/components/ui/primitives";
import { Select } from "@/components/ui/select";
import { EmptyState, ErrorState, FormError, LoadingBlock } from "@/components/ui/states";
import { formatDate, formatHours, formatLiters, formatNumber, toDateValue } from "@/lib/format";
import { useI18n } from "@/i18n/provider";

/**
 * Reports. A date range, the totals it produces, and a file you can send to a CFO.
 *
 * Exports are asynchronous by design (the spec renders them on a worker: a year of refills
 * is not a request/response job), so the UI hands back a job and polls it. The download is
 * a plain link, not an XHR — the browser is better at saving files than we are.
 */

const EXPORT_TONE: Record<ReportExportStatus, "ok" | "low" | "dry" | "idle"> = {
  ready: "ok",
  pending: "low",
  failed: "dry",
  expired: "idle",
};

function startOfMonth(now: Date): Date {
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
}

function daysAgo(days: number): Date {
  return new Date(Date.now() - days * 86_400_000);
}

export function ReportsPage() {
  const { t, lang } = useI18n();

  const [from, setFrom] = useState(() => toDateValue(startOfMonth(new Date())));
  const [to, setTo] = useState(() => toDateValue(new Date()));

  const window_ = {
    from: new Date(`${from}T00:00:00Z`).toISOString(),
    // The API's `to` is EXCLUSIVE. A user picking "31 July" means "including the 31st", so
    // we push the bound to the start of the next day — otherwise the last day silently
    // vanishes from every report, which is the kind of bug a finance team finds first.
    to: new Date(new Date(`${to}T00:00:00Z`).getTime() + 86_400_000).toISOString(),
  };

  const summaryQuery = useReportSummary(window_);
  const exportsQuery = useReportExports();
  const createExport = useCreateReportExport();

  const [report, setReport] = useState<ReportType>("refills");
  const [format, setFormat] = useState<ReportFormat>("xlsx");

  const presets: Array<{ label: string; from: Date; to: Date }> = [
    { label: t("reports.range.30"), from: daysAgo(30), to: new Date() },
    { label: t("reports.range.90"), from: daysAgo(90), to: new Date() },
    { label: t("reports.range.month"), from: startOfMonth(new Date()), to: new Date() },
  ];

  const totals = summaryQuery.data?.totals;

  return (
    <>
      <PageHeader title={t("reports.title")} />

      <Card className="mb-4">
        <CardBody className="flex flex-wrap items-end gap-4">
          <Field label={t("reports.from")} className="w-44">
            {(props) => (
              <input
                {...props}
                type="date"
                dir="ltr"
                value={from}
                max={to}
                onChange={(event) => setFrom(event.target.value)}
                className="h-10 w-full rounded-[6px] border border-rule-strong bg-paper px-3 font-mono text-sm text-text focus-visible:border-ink focus-visible:outline-2 focus-visible:outline-offset-[-1px] focus-visible:outline-ink"
              />
            )}
          </Field>

          <Field label={t("reports.to")} className="w-44">
            {(props) => (
              <input
                {...props}
                type="date"
                dir="ltr"
                value={to}
                min={from}
                max={toDateValue(new Date())}
                onChange={(event) => setTo(event.target.value)}
                className="h-10 w-full rounded-[6px] border border-rule-strong bg-paper px-3 font-mono text-sm text-text focus-visible:border-ink focus-visible:outline-2 focus-visible:outline-offset-[-1px] focus-visible:outline-ink"
              />
            )}
          </Field>

          <div className="flex flex-wrap gap-2 pb-0.5">
            {presets.map((preset) => (
              <Button
                key={preset.label}
                variant="secondary"
                size="sm"
                onClick={() => {
                  setFrom(toDateValue(preset.from));
                  setTo(toDateValue(preset.to));
                }}
              >
                {preset.label}
              </Button>
            ))}
          </div>
        </CardBody>
      </Card>

      {summaryQuery.isPending ? (
        <Card>
          <LoadingBlock />
        </Card>
      ) : summaryQuery.error ? (
        <Card>
          <ErrorState error={summaryQuery.error} onRetry={() => void summaryQuery.refetch()} />
        </Card>
      ) : (
        <>
          <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Stat
              label={t("dash.litersAdded")}
              value={formatLiters(lang, totals?.liters_added ?? 0)}
            />
            <Stat
              label={t("dash.refillsRecorded")}
              value={formatNumber(lang, totals?.refills_recorded ?? 0)}
            />
            <Stat
              label={t("gen.runningHours")}
              value={formatHours(lang, totals?.running_hours ?? 0)}
            />
            <Stat
              label={t("dash.litersPerHour")}
              value={
                totals?.liters_per_hour !== undefined
                  ? formatNumber(lang, totals.liters_per_hour, 1)
                  : "—"
              }
            />
          </div>

          <Card className="mb-4">
            <CardHeader>
              <CardTitle>{t("reports.perSite")}</CardTitle>
              <ExportButton
                report={report}
                format={format}
                setReport={setReport}
                setFormat={setFormat}
                window_={window_}
                createExport={createExport}
              />
            </CardHeader>

            {(summaryQuery.data?.sites.length ?? 0) === 0 ? (
              <EmptyState icon={<FileBarChart className="size-6" />} title={t("reports.noData")} />
            ) : (
              <div className="w-full overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <caption className="sr-only">{t("reports.perSite")}</caption>
                  <thead>
                    <tr className="border-b border-rule bg-paper-sunk">
                      {[
                        t("refills.site"),
                        t("dash.refillsRecorded"),
                        t("dash.litersAdded"),
                        t("gen.runningHours"),
                        t("dash.litersPerHour"),
                        t("sites.lastRefill"),
                      ].map((heading) => (
                        <th
                          key={heading}
                          scope="col"
                          className="label-caps px-3 py-2 text-start font-mono text-[10px] font-medium text-faint"
                        >
                          {heading}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {summaryQuery.data?.sites.map((row) => (
                      <tr key={row.site.id} className="border-b border-rule last:border-b-0">
                        <td className="px-3 py-2.5 font-medium text-text">{row.site.name}</td>
                        <td className="px-3 py-2.5">
                          <Num>{formatNumber(lang, row.refills_recorded)}</Num>
                        </td>
                        <td className="px-3 py-2.5">
                          <Num className="font-medium">{formatLiters(lang, row.liters_added)}</Num>
                        </td>
                        <td className="px-3 py-2.5 text-muted">
                          <Num>
                            {row.running_hours !== undefined
                              ? formatHours(lang, row.running_hours)
                              : "—"}
                          </Num>
                        </td>
                        <td className="px-3 py-2.5 text-muted">
                          <Num>
                            {row.liters_per_hour !== undefined
                              ? formatNumber(lang, row.liters_per_hour, 1)
                              : "—"}
                          </Num>
                        </td>
                        <td className="px-3 py-2.5 text-xs text-muted">
                          <Num>
                            {row.last_refill_at ? formatDate(lang, row.last_refill_at) : "—"}
                          </Num>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}

      {/* ---- the export queue ---- */}
      <Card>
        <CardHeader>
          <CardTitle>{t("reports.exports")}</CardTitle>
        </CardHeader>

        {exportsQuery.isPending ? (
          <LoadingBlock />
        ) : (exportsQuery.data?.data.length ?? 0) === 0 ? (
          <EmptyState title={t("reports.exportsEmpty")} />
        ) : (
          <CardBody className="flex flex-col gap-2">
            {exportsQuery.data?.data.map((job) => (
              <div
                key={job.id}
                className="flex flex-wrap items-center justify-between gap-3 border-b border-rule pb-2 last:border-b-0 last:pb-0"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-text">
                    {job.filename ?? t(`reports.type.${job.report}` as const)}
                  </p>
                  <p className="text-xs text-faint">
                    <Num>
                      {formatDate(lang, job.from)} — {formatDate(lang, job.to)}
                    </Num>
                    {" · "}
                    <span className="font-mono uppercase">{job.format}</span>
                  </p>
                </div>

                <div className="flex shrink-0 items-center gap-3">
                  <Badge tone={EXPORT_TONE[job.status]}>
                    {job.status === "pending" ? (
                      <Loader2 className="size-3 animate-spin" aria-hidden />
                    ) : null}
                    {t(`reports.export.${job.status}` as const)}
                  </Badge>

                  {job.status === "ready" ? (
                    // A real link with `download` — the browser saves the file; we do not
                    // buffer megabytes of XLSX through JS to achieve the same thing.
                    <Button asChild size="sm" variant="secondary">
                      <a href={reportDownloadUrl(job.id)} download={job.filename}>
                        <Download className="size-4" aria-hidden />
                        {t("reports.download")}
                      </a>
                    </Button>
                  ) : null}
                </div>
              </div>
            ))}
          </CardBody>
        )}
      </Card>
    </>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardBody>
        <MicroLabel>{label}</MicroLabel>
        <p className="mt-2 font-mono text-2xl font-medium text-text">
          <Num>{value}</Num>
        </p>
      </CardBody>
    </Card>
  );
}

function ExportButton({
  report,
  format,
  setReport,
  setFormat,
  window_,
  createExport,
}: {
  report: ReportType;
  format: ReportFormat;
  setReport: (value: ReportType) => void;
  setFormat: (value: ReportFormat) => void;
  window_: { from: string; to: string };
  createExport: ReturnType<typeof useCreateReportExport>;
}) {
  const { t } = useI18n();

  return (
    <div className="flex flex-wrap items-center gap-2">
      <FormError error={createExport.error} />

      <div className="w-44">
        <Select
          value={report}
          onValueChange={(value) => setReport(value as ReportType)}
          aria-label={t("reports.type")}
          options={[
            { value: "refills", label: t("reports.type.refills") },
            { value: "consumption", label: t("reports.type.consumption") },
            { value: "sites", label: t("reports.type.sites") },
          ]}
        />
      </div>

      <div className="w-28">
        <Select
          value={format}
          onValueChange={(value) => setFormat(value as ReportFormat)}
          aria-label={t("reports.format")}
          options={[
            { value: "xlsx", label: "Excel" },
            { value: "pdf", label: "PDF" },
          ]}
        />
      </div>

      <Button
        size="sm"
        disabled={createExport.isPending}
        onClick={() => createExport.mutate({ report, format, ...window_ })}
      >
        <Download className="size-4" aria-hidden />
        {createExport.isPending ? t("common.saving") : t("reports.export")}
      </Button>
    </div>
  );
}
